class AudioManager {
    constructor() {
        this.isInitialized = false;
        this.sounds = {};
        this.initializeSounds();
    }

    initializeSounds() {
        this.audioContext = null;

        this.sounds = {
            success: this.createTone(800, 0.3, 'sine'),
            double_beep: this.createDoubleBeep(),
            error: this.createTone(300, 0.5, 'sawtooth')
        };
    }

    createTone(frequency, duration, type = 'sine') {
        return () => {
            if (!this.audioContext) {
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            }

            const oscillator = this.audioContext.createOscillator();
            const gainNode = this.audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(this.audioContext.destination);

            oscillator.frequency.value = frequency;
            oscillator.type = type;

            gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.3, this.audioContext.currentTime + 0.01);
            gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration);

            oscillator.start(this.audioContext.currentTime);
            oscillator.stop(this.audioContext.currentTime + duration);
        };
    }

    createDoubleBeep() {
        return () => {
            this.createTone(600, 0.2)();
            setTimeout(() => this.createTone(600, 0.2)(), 300);
        };
    }

    async initializeAudio() {
        try {
            if (!this.audioContext) {
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            }

            if (this.audioContext.state === 'suspended') {
                await this.audioContext.resume();
            }

            this.isInitialized = true;
            return true;
        } catch (error) {
            console.warn('Audio initialization failed:', error);
            return false;
        }
    }

    async playSound(type) {
        if (!this.isInitialized) {
            await this.initializeAudio();
        }

        try {
            const soundFunction = this.sounds[type] || this.sounds.error;
            soundFunction();
            return true;
        } catch (error) {
            console.warn('Sound play failed:', error);
            this.systemBeep();
            return false;
        }
    }

    systemBeep() {
        const audio = new Audio();
        audio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IAAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+Pwtm=';
        audio.volume = 0.3;
        audio.play().catch(() => {
            console.log('Audio not available');
        });
    }
}

document.addEventListener('DOMContentLoaded', function () {
    
    let palletCheckboxState = "unchecked";
    let carrierTypePallet = null;

    const audioManager = new AudioManager();
    let palletData = null;
    const elements = {
        trackingInput: document.getElementById('trackingInput'),
        form: document.getElementById('scanForm'),
        statusMessage: document.getElementById('statusMessage'),
        loadingSpinner: document.getElementById('loadingSpinner'),
        soundIndicator: document.getElementById('soundIndicator'),
        audioInitOverlay: document.getElementById('audioInitOverlay')
    };

    const carrierClasses = {
        fedex: 'carrier-fedex',
        ups: 'carrier-ups',
        usps: 'carrier-usps',
        dhl: 'carrier-dhl',
    };

    const getCarrierClass = type => carrierClasses[type] || 'carrier-unknown';
    const formatCarrierName = type => (type || 'unknown').toUpperCase();
    const createCarrierBadge = type =>
        `<span class="carrier-indicator ${getCarrierClass(type)}">${formatCarrierName(type)}</span>`;

    const createTrackerDetails = tracker => `
                <div class="tracker-details">
                    <strong>Tracking:</strong> ${tracker.tracking_number}<br>
                    <strong>Carrier:</strong> ${tracker.carrier.carrier_type.name}<br>
                    <strong>Verified:</strong> ${tracker.confirmation_date}<br>
                    <strong>Order Number:</strong> ${tracker?.order?.orderNumber ?? 'N/A'}<br>
                    <strong>Package Name:</strong> ${tracker?.package?.name ?? 'N/A'}<br>
                </div>`;

    const state = {
        showLoading: (show) => {
            if (elements.loadingSpinner) {
                elements.loadingSpinner.style.display = show ? 'block' : 'none';
            }
        },

        showMessage: (show) => {
            elements.statusMessage.style.display = show ? 'block' : 'none';
        },

        displayMessage: (message, type, tracker = null) => {
            const fullMessage = tracker ?
                message + createTrackerDetails(tracker) :
                message;

            elements.statusMessage.innerHTML = fullMessage;
            elements.statusMessage.className = `status-message status-${type}`;
            state.showMessage(true);

            setTimeout(() => state.showMessage(false), 30000);
        },

        playSound: async (type) => {
            const success = await audioManager.playSound(type);
            if (success) {
                state.showSoundIndicator(type);
            }
        },

        showSoundIndicator: (type) => {
            if (!type) return;
            const indicators = {
                success: '🔊 Success',
                double_beep: '🔊 Alert',
                error: '🔊 Error'
            };
            elements.soundIndicator.textContent = indicators[type] || '🔊 Sound';
            elements.soundIndicator.style.display = 'block';
            setTimeout(() => {
                elements.soundIndicator.style.display = 'none';
            }, 3000);
        },

        clearInput: () => {
            elements.trackingInput.value = '';
            elements.trackingInput.focus();
        }
    };

    const scanForPackage = (trackingNumber) => {
        if (!trackingNumber || trackingNumber.trim().length === 0) {
            state.displayMessage('Please enter a tracking number', 'error');
            return;
        }

        const url = elements.form.getAttribute('data-scan-url');
        if (!url) {
            state.displayMessage('Configuration error: scan URL not found', 'error');
            return;
        }

        const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

        const requestData = {
            pallet_id: palletData ? palletData.id : null,
            tracking_number: trackingNumber.trim(),
            _token: token
        };

        state.showLoading(true);

        fetch(url, {
            method: 'POST',
            headers: {
                'X-CSRF-TOKEN': token,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestData)
        })
            .then(response => {
                if (response.ok || response.status === 404 || response.status === 422 || response.status === 500) {
                    return response.json();
                } else {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            })
            .then(data => {
                state.showLoading(false);
                handleScanResponse(data);
            })
            .catch(error => {
                state.showLoading(false);
                state.displayMessage('Request failed: ' + error.message, 'error');
                state.playSound('error');
            });
    };

    const handleScanResponse = async (data) => {
        const tracker = data.data ? data.data.tracker : null;
        const carrierType = tracker && tracker.carrier ? tracker.carrier.carrier_type.name : null;

        switch (data.status) {
            case 'confirmed':
                state.displayMessage(`Package Verified Successfully! ${createCarrierBadge(carrierType)}`, 'success', tracker);
                await state.playSound('success');
                break;
            case 'already_confirmed':
                state.displayMessage(`Package Already Verified! ${createCarrierBadge(carrierType)}`, 'warning', tracker);
                await state.playSound('double_beep');
                break;
            case 'not_found':
                state.displayMessage('Tracking Number Not Found!', 'error');
                await state.playSound('error');
                break;
            case 'error':
                state.displayMessage('Error: ' + (data.message || 'Unknown error'), 'error');
                await state.playSound('error');
                break;
            default:
                state.displayMessage('Unknown response: ' + data.status, 'error');
                await state.playSound('error');
        }

        setTimeout(() => {
            if(palletCheckboxState === 'unchecked') {
                palletState.showMessage(false)
                palletState.clearInput();
            }
            state.clearInput();
        }, 3000);
    };

    if (elements.form) {
        elements.form.addEventListener('submit', function (e) {
            e.preventDefault();
            const trackingNumber = elements.trackingInput.value.trim();
            if (trackingNumber) {
                scanForPackage(trackingNumber);
            } else {
                state.displayMessage('Please enter a tracking number', 'error');
            }
        });
    }
   

    // elements.trackingInput.addEventListener('keypress', function (e) {
    //     if (e.key === 'Enter') {
    //         e.preventDefault();
    //         elements.form.dispatchEvent(new Event('submit'));
    //     }
    // });

    // elements.trackingInput.addEventListener('blur', () => {
    //     setTimeout(() => elements.trackingInput.focus(), 50);
    // });

    // document.addEventListener('keydown', (e) => {
    //     if (e.ctrlKey || e.metaKey || e.altKey) return;

    //     if (e.key.match(/^[a-zA-Z0-9]$/)) {
    //         elements.trackingInput.focus();
    //     }
    // });

    if (elements.audioInitOverlay) {
        elements.audioInitOverlay.addEventListener('click', async () => {
            const success = await audioManager.initializeAudio();
            if (success) {
                elements.audioInitOverlay.style.display = 'none';
                await audioManager.playSound('success');
                state.showSoundIndicator('success');
            }
        });
    }

    // elements.trackingInput.focus();
    state.showLoading(false);

    // setInterval(() => {
    //     if (document.activeElement !== elements.trackingInput &&
    //         (!elements.audioInitOverlay || elements.audioInitOverlay.style.display === 'none')) {
    //         elements.trackingInput.focus();
    //     }
    // }, 1000);

    const createPalletDetails = (pallet, badge, type) => `
                <span>
                <strong>Carrier:</strong>${badge}. 
                <strong>Pallet ID:</strong> ${pallet.id}. 
                <strong>${type == 'success'? 'Today' : ''} Date:</strong> ${pallet.date.split(' ')[0]}
                </span>`;

    const palletElements = {
        form: document.getElementById('palletScanForm'),
        input: document.getElementById('palletInput'),
        statusMessage: document.getElementById('palletStatusMessage'),
        loadingSpinner: document.getElementById('palletLoadingSpinner'),
        pallectCheckbox: document.getElementById('pallet_checkbox'),
    };
    

    const palletState = {
        showLoading: (show) => {
            palletElements.loadingSpinner.style.display = show ? 'block' : 'none';
        },

        showMessage: (show) => {
            palletElements.statusMessage.style.display = show ? 'block' : 'none';
        },

        displayMessage: (message, type) => {            
            let fullMessage = message;
            palletElements.statusMessage.innerHTML = fullMessage;
            palletElements.statusMessage.className = `pallet-status-message status-${type}`;
            palletState.showMessage(true);

        },

        clearInput: () => {
            palletElements.input.value = '';
            palletElements.input.focus();
        },

        playSound: async (type) => {
            const success = await audioManager.playSound(type);
            if (success) {
                state.showSoundIndicator(type); // reuse from tracking logic
            }
        }
    };

    const scanForPallet = (palletNumber) => {
        if (!palletNumber || palletNumber.trim().length === 0) {
            palletState.displayMessage('Please enter a pallet number', 'error');
            return;
        }

        const url = palletElements.form.getAttribute('data-scan-url');
        const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

        const requestData = {
            pallet_number: palletNumber.trim(),
            _token: token,
        };

        palletState.showLoading(true);

        fetch(url, {
            method: 'POST',
            headers: {
                'X-CSRF-TOKEN': token,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestData)
        })
        .then(response => response.json())
        .then(data => {
            palletState.showLoading(false);
            handlePalletResponse(data);
        })
        .catch(error => {
            palletState.showLoading(false);
            palletState.displayMessage('Request failed: ' + error.message, 'error');
            palletState.playSound('error');
        });
    };

    const palletType = (pallet) => {
        if (pallet) {
            const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
            const palletDate = pallet.date
            ? pallet.date.split(' ')[0] 
            : null;
            var type = palletDate === today ? 'success' : 'warning';
            return type;
        } else {
            var type = 'error';
            return type;
        }
    }

    const handlePalletResponse = async (data) => {
        
        const pallet = data.data ? data.data.pallet : null;
        palletData = pallet;
        carrierTypePallet = pallet && pallet.carrier ? pallet.carrier.name : null;
        let message = '';
        let type = palletType(pallet);

        switch (data.status) {
            case 'confirmed':
                let badge = createCarrierBadge(carrierTypePallet);
                palletState.displayMessage(`${createPalletDetails(pallet, badge, type)}`, type);
                elements.trackingInput.focus();
                await palletState.playSound(type);
                break;
            case 'not_found':
                message = 'Pallet Does not exist!';
                palletState.displayMessage(message, type);
                await palletState.playSound(type);
                break;
            case 'error':
                message = 'Error: ' + (data.message || 'Unknown error');
                palletState.displayMessage(message, type);
                await palletState.playSound(type);
                break;
            default:
                message = 'Unknown response: ' + data.status;
                type = 'error';
                palletState.displayMessage(message, type);
                await palletState.playSound(type);
        }
        
    };

    if (palletElements.form && palletElements.input) {
        palletElements.form.addEventListener('submit', function (e) {
            e.preventDefault();
            const palletNumber = palletElements.input.value.trim();
            if (palletNumber) {
                scanForPallet(palletNumber);
            } else {
                palletState.displayMessage('Please enter a pallet number', 'error');
            }
        });

        // palletElements.input.addEventListener('keypress', function (e) {
        //     if (e.key === 'Enter') {
        //         e.preventDefault();
        //         palletElements.form.dispatchEvent(new Event('submit'));
        //     }
        // });

        palletState.showLoading(false);
    }

    if (palletElements.pallectCheckbox) {
        palletElements.pallectCheckbox.addEventListener('change', function () {
            palletCheckboxState = sessionStorage.setItem('palletCheckboxState', this.checked ? 'checked' : 'unchecked');
        });
    }

});
